// Admin › Help › one article's device check, made on a phone beside the Scout:
// walk each step on the Scout, tick each step and label that matches, then pass
// or fail. The API checks the ticks against the revision's checklist and
// records the Scout's software from its newest update check; a pass moves the
// revision from ready for check to checked. Takes the whole window.
// docs/wip/SCOUT_HELP.md

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { APP_NAMES, parseInline } from '@vortex/help-render';
import { api } from '../../../services/api';
import { TickBox } from './TickBox';
import { formatRelativeTime } from '../../../utils/formatters';
import { checkReceivedAt, type UpdateCheckRow } from '../../../utils/deviceSoftware';
import {
  STATE_LABELS,
  atLeast,
  checklistGroups,
  errorText,
  softwareOf,
  useDeviceCheck,
  useHelpArticle,
  type ChecklistItem,
  type HelpDeviceCheck,
  type NeedsSoftware,
} from './data/help';

const input = 'min-h-[44px] w-full rounded-[10px] border border-slate-400/20 bg-slate-900/60 px-3 text-[15px] text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/40';
const button = 'inline-flex min-h-[48px] items-center justify-center rounded-[10px] px-4 text-[15px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:opacity-40';

/** The Scout last checked on, kept in this browser for the next check. */
const STORE = 'help.checkScout';
const remembered = () => { try { return localStorage.getItem(STORE) ?? ''; } catch { return ''; } };
const remember = (id: string) => { try { localStorage.setItem(STORE, id); } catch { /* private window */ } };

/** Step text with its bold labels, as the Scout shows them. */
const Inline = ({ text }: { text: string }) => <>{parseInline(text).spans.map((s, i) => (s.bold ? <b key={i} className="font-semibold text-white">{s.text}</b> : <span key={i}>{s.text}</span>))}</>;

/** As the Scout's own Updates dialog names it: PolarisOS, not "PolarisOS (launcher)". */
const appName = (key: string) => (key === 'launcher' ? 'PolarisOS' : APP_NAMES[key] ?? key);
const softwareLine = (software: Record<string, string>, keys: string[]) => keys.filter((k) => software[k]).map((k) => `${appName(k)} ${software[k]}`).join(' · ');

export function HelpCheckPage() {
  const { id } = useParams();
  const detail = useHelpArticle(id);
  const check = useDeviceCheck(id);
  const [scoutId, setScoutId] = useState(remembered);
  const [ticks, setTicks] = useState<Set<string>>(new Set());
  const [failedStep, setFailedStep] = useState('');
  const [note, setNote] = useState('');
  const [needNote, setNeedNote] = useState(false);
  const noteBox = useRef<HTMLTextAreaElement>(null);

  const revision = detail.data?.revision ?? null;
  // A new revision starts a new walk.
  useEffect(() => { setTicks(new Set()); }, [revision?.id]);

  const article = detail.data?.article;
  if (detail.isLoading) return <Shell><p className="text-[15px] text-slate-400">Loading…</p></Shell>;
  if (detail.isError || !article || !revision) return <Shell><p className="text-[15px] text-red-400">Couldn't load the article.</p></Shell>;

  const items = detail.data!.checklist;
  const steps = items.filter((i) => i.kind === 'step').length;
  const done = check.isSuccess ? check.data.result : null;
  const ready = revision.state === 'ready' && !done;
  const all = items.length > 0 && items.every((i) => ticks.has(i.key));
  const toggle = (key: string) => setTicks((t) => { const n = new Set(t); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const send = (result: 'pass' | 'fail') => {
    if (result === 'fail' && !note.trim()) {
      setNeedNote(true);
      noteBox.current?.focus();
      return;
    }
    setNeedNote(false);
    check.mutate({
      revisionId: revision.id,
      body: {
        deviceId: scoutId, result, ticks: items.map((i) => i.key).filter((k) => ticks.has(k)),
        failedStep: result === 'fail' && failedStep ? Number(failedStep) : null, note: result === 'fail' ? note.trim() : null,
      },
    });
  };

  return (
    <div className="admin-console flex min-h-screen flex-col bg-slate-900 text-slate-300">
      <header className="sticky top-0 z-10 flex items-center gap-2 border-b border-slate-400/15 bg-slate-900/95 px-3 py-1.5 backdrop-blur">
        <Link to={`/admin/help/articles/${article.id}`} className="inline-flex min-h-[44px] items-center gap-1.5 px-1 text-[15px] font-medium text-slate-400 hover:text-white">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />Editor
        </Link>
        <h1 className="text-[17px] font-semibold text-white">Device check</h1>
      </header>

      <main className="mx-auto w-full max-w-xl flex-1 space-y-5 px-4 pb-8 pt-4">
        <div>
          <p className="font-mono text-[15px] text-slate-400">{article.topicId} · revision {revision.number}</p>
          <h2 className="mt-1 text-[21px] font-semibold text-white">{revision.title}</h2>
        </div>

        {done === 'pass' && <p role="status" className="rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-[15px] text-emerald-100">Revision {revision.number} passed on {check.data!.deviceName}. It can be published.</p>}
        {done === 'fail' && <p role="status" className="rounded-xl border border-amber-400/30 bg-amber-400/10 px-4 py-3 text-[15px] text-amber-100">Recorded as failed. Fix the article in the editor, save it and mark it ready for check again.</p>}

        {!ready && !done && (
          <div className="space-y-2 rounded-xl border border-slate-400/15 bg-slate-800/45 px-4 py-3">
            <p className="text-[15px] text-slate-200">{notReady(revision.number, revision.state)}</p>
            <Link to={`/admin/help/articles/${article.id}`} className="inline-flex min-h-[44px] items-center text-[15px] font-semibold text-cyan-300 hover:text-cyan-200">Open the editor</Link>
          </div>
        )}

        {ready && (
          <>
            <ScoutCard scoutId={scoutId} needs={revision.needsSoftware} onChoose={(d) => { setScoutId(d); if (d) remember(d); }} />

            <section aria-labelledby="walk" className="space-y-2">
              <h3 id="walk" className="text-[15px] font-semibold text-slate-300">Walk each step on the Scout and tick what matches</h3>
              <div className="overflow-hidden rounded-xl border border-slate-400/15 bg-slate-800/45">
                {checklistGroups(items).map(({ item, labels }) => (
                  <div key={item.key} role="group" aria-label={item.kind === 'step' ? `Step ${item.key.slice(5)}` : item.text} className="border-t border-slate-400/10 first:border-t-0">
                    <Tick item={item} on={ticks.has(item.key)} onToggle={() => toggle(item.key)} />
                    {labels.map((l) => <Tick key={l.key} item={l} on={ticks.has(l.key)} onToggle={() => toggle(l.key)} indent />)}
                  </div>
                ))}
              </div>
            </section>

            <section aria-labelledby="failing" className="space-y-3">
              <h3 id="failing" className="text-[15px] font-semibold text-slate-300">If something did not match</h3>
              {steps > 0 && (
                <label className="flex flex-col gap-1.5 text-[15px] font-medium text-slate-300">
                  The step that failed
                  <select className={input} value={failedStep} onChange={(e) => setFailedStep(e.target.value)}>
                    <option value="">Not one step</option>
                    {Array.from({ length: steps }, (_, n) => <option key={n} value={n + 1}>Step {n + 1}</option>)}
                  </select>
                </label>
              )}
              <label className="flex flex-col gap-1.5 text-[15px] font-medium text-slate-300">
                What did not match
                <textarea ref={noteBox} rows={3} className={`${input} py-2.5`} value={note} onChange={(e) => { setNote(e.target.value); if (e.target.value.trim()) setNeedNote(false); }} />
              </label>
            </section>
          </>
        )}

        <Earlier checks={detail.data!.deviceChecks} />
      </main>

      {ready && (
        <footer className="sticky bottom-0 border-t border-slate-400/15 bg-slate-900/95 px-4 pb-4 pt-3 backdrop-blur">
          <div className="mx-auto max-w-xl space-y-2.5">
            {needNote && <p role="alert" className="text-[15px] text-amber-300">Say what did not match in the note to fail it.</p>}
            {check.isError && <p role="alert" className="text-[15px] text-red-300">{errorText(check.error)}</p>}
            {!scoutId && <p className="text-[15px] text-slate-400">Choose the Scout first.</p>}
            <p className="text-[15px] text-slate-400">{items.filter((i) => ticks.has(i.key)).length} of {items.length} ticked</p>
            <div className="flex gap-2.5">
              <button type="button" disabled={!scoutId || check.isPending} onClick={() => send('fail')} className={`${button} flex-1 border border-red-400/40 bg-red-400/[0.08] text-red-200 hover:bg-red-400/15`}>Fail</button>
              <button type="button" disabled={!scoutId || !all || check.isPending} onClick={() => send('pass')} className={`${button} flex-[2] bg-cyan-600 text-white hover:bg-cyan-500`}>Pass</button>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
}

function notReady(n: number, state: keyof typeof STATE_LABELS): string {
  if (state === 'draft') return `Revision ${n} is a draft. Mark it ready for check in the editor first.`;
  if (state === 'checked') return `Revision ${n} has passed its device check.`;
  return `Revision ${n} is ${STATE_LABELS[state].toLowerCase()}.`;
}

function Tick({ item, on, onToggle, indent }: { item: ChecklistItem; on: boolean; onToggle: () => void; indent?: boolean }) {
  const name = item.kind === 'step' ? `Step ${item.key.slice(5)}` : `${item.text} as written`;
  return (
    <label className={`flex min-h-[48px] cursor-pointer items-start gap-3 py-3 pr-4 text-[15px] leading-snug ${indent ? 'pl-12 pt-0 text-slate-300' : 'pl-4 text-slate-200'}`}>
      <TickBox label={name} checked={on} onChange={onToggle} className="mt-0.5" />
      <span className="min-w-0">
        {item.kind === 'step' ? <><span className="font-mono text-slate-400">{item.key.slice(5)} · </span><Inline text={item.text} /></> : <><b className="font-semibold text-white">{item.text}</b> as written</>}
      </span>
    </label>
  );
}

function ScoutCard({ scoutId, needs, onChoose }: { scoutId: string; needs: NeedsSoftware[]; onChoose: (id: string) => void }) {
  const [finding, setFinding] = useState(!scoutId);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [picked, setPicked] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => { const t = window.setTimeout(() => setDebounced(query.trim()), 300); return () => window.clearTimeout(t); }, [query]);

  const found = useQuery({ queryKey: ['help', 'check', 'scouts', debounced], queryFn: () => api.adminGetDevices({ search: debounced, limit: 8, page: 1 }), enabled: finding && debounced.length >= 2 });
  const device = useQuery({ queryKey: ['admin', 'device', scoutId], queryFn: () => api.adminGetDevice(scoutId), enabled: !!scoutId && picked?.id !== scoutId });
  // The newest update check: the same row the API records the software from.
  const latest = useQuery({ queryKey: ['help', 'check', 'software', scoutId], queryFn: () => api.adminGetNeptuneUpdates({ device_id: scoutId, limit: 1 }), enabled: !!scoutId });

  const name = picked?.id === scoutId ? picked.name : device.data ? device.data.name || device.data.serial_number : null;
  const row = latest.data?.updates[0] as UpdateCheckRow | undefined;
  const software = softwareOf(row?.appDetails);
  const at = checkReceivedAt(row);
  const keys = ['launcher', ...needs.map((n) => n.app).filter((k) => k !== 'launcher')];
  const short = needs.filter((n) => software[n.app] && !atLeast(software[n.app], n.version));
  const unknown = needs.filter((n) => row && !software[n.app]);

  if (finding || !scoutId) {
    return (
      <section aria-label="Scout" className="space-y-2">
        <label className="flex flex-col gap-1.5 text-[15px] font-medium text-slate-300">
          Find the Scout
          <input className={input} value={query} placeholder="Boat name, serial or owner" autoComplete="off" onChange={(e) => setQuery(e.target.value)} />
        </label>
        {debounced.length >= 2 && (
          <ul className="overflow-hidden rounded-xl border border-slate-400/15 bg-slate-800/45">
            {found.isFetching && <li className="px-4 py-3 text-[15px] text-slate-400">Searching…</li>}
            {found.isError && <li className="px-4 py-3 text-[15px] text-red-300">The search failed.</li>}
            {found.data && !found.isFetching && found.data.devices.length === 0 && <li className="px-4 py-3 text-[15px] text-slate-400">No Scout matches.</li>}
            {(found.data?.devices ?? []).map((d) => (
              <li key={d.id} className="border-t border-slate-400/10 first:border-t-0">
                <button type="button" className="flex min-h-[52px] w-full flex-col items-start px-4 py-2.5 text-left hover:bg-slate-400/10" onClick={() => { setPicked({ id: d.id, name: d.name || d.serial_number }); onChoose(d.id); setFinding(false); setQuery(''); }}>
                  <span className="text-[15px] font-semibold text-white">{d.name || d.serial_number}</span>
                  <span className="text-[14px] text-slate-400">{d.serial_number}{d.user?.name || d.user?.email ? ` · ${d.user?.name || d.user?.email}` : ''}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
        {scoutId && <button type="button" className="min-h-[44px] text-[15px] font-medium text-slate-400 hover:text-white" onClick={() => setFinding(false)}>Keep the Scout I had</button>}
      </section>
    );
  }

  return (
    <section aria-label="Scout" className="space-y-1.5 rounded-xl border border-slate-400/15 bg-slate-800/45 px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="flex-1 text-[15px] font-semibold text-white">{name ?? (device.isError ? 'Unknown Scout' : 'Loading…')}</span>
        <button type="button" className="min-h-[44px] px-1 text-[15px] font-semibold text-cyan-300 hover:text-cyan-200" onClick={() => setFinding(true)}>Change</button>
      </div>
      {latest.isLoading && <p className="text-[15px] text-slate-400">Loading its software…</p>}
      {latest.data && !row && <p className="text-[15px] text-amber-200">No update check from this Scout yet, so the check records no software.</p>}
      {row && (
        <>
          <p className="font-mono text-[15px] text-slate-200">{softwareLine(software, keys) || 'No Vortex apps in its update check'}</p>
          <p className="text-[14px] text-slate-400">From its update check {at ? formatRelativeTime(at.toISOString()) : 'at an unknown time'}</p>
        </>
      )}
      {short.map((n) => <p key={n.app} className="text-[15px] text-amber-200">The article needs {appName(n.app)} {n.version} or later; this Scout has {software[n.app]}.</p>)}
      {unknown.map((n) => <p key={n.app} className="text-[15px] text-amber-200">Its update check doesn't list {appName(n.app)}, which the article needs.</p>)}
    </section>
  );
}

function Earlier({ checks }: { checks: HelpDeviceCheck[] }) {
  if (!checks.length) return null;
  return (
    <section aria-label="Checks so far" className="space-y-2">
      <h3 className="text-[15px] font-semibold text-slate-300">Checks so far</h3>
      <ul className="overflow-hidden rounded-xl border border-slate-400/15 bg-slate-800/45">
        {checks.map((c) => (
          <li key={c.id} className="space-y-1 border-t border-slate-400/10 px-4 py-3 first:border-t-0">
            <p className="text-[15px] text-slate-200">
              <span className={c.result === 'pass' ? 'font-semibold text-emerald-300' : 'font-semibold text-amber-300'}>{c.result === 'pass' ? 'Passed' : c.failedStep ? `Failed at step ${c.failedStep}` : 'Failed'}</span>
              {' · '}{c.checkedByName} · {c.deviceName}{Object.keys(c.software).length ? ` · ${softwareLine(c.software, Object.keys(c.software))}` : ''} · {formatRelativeTime(c.createdAt)}
            </p>
            {c.note && <p className="text-[15px] text-slate-400">{c.note}</p>}
          </li>
        ))}
      </ul>
    </section>
  );
}

function Shell({ children }: { children: ReactNode }) {
  return (
    <div className="admin-console min-h-screen bg-slate-900 px-4 py-6 text-slate-300">
      <Link to="/admin/help/articles" className="mb-6 inline-flex min-h-[44px] items-center gap-1.5 text-[15px] font-medium text-slate-400 hover:text-white">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />Articles
      </Link>
      {children}
    </div>
  );
}
